import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TestResult } from '../../../shared/test.types';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { TranslationService } from '../../../../services/translation.service';
import {
  COLORS_MAP,
  DelayedRecallResult,
  RECALL_POOLS,
  RecallItems,
  RecallResponses,
} from '../../sharp-v2.types';
import { SharpAudioService } from '../../services/sharp-audio.service';
import { SharpScoringService } from '../../services/sharp-scoring.service';
import { SharpStateService } from '../../services/sharp-state.service';

type Phase = 'free' | 'cued' | 'review' | 'done';
type FieldKey = keyof RecallItems;

interface CuedQuestion {
  key: FieldKey;
  options: string[];
}

@Component({
  selector: 'app-memory-scene',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './memory-scene.component.html',
  styleUrls: ['./memory-scene.component.scss'],
})
export class MemorySceneComponent implements OnInit, OnDestroy {
  @Input() testId = 'memory-scene';
  @Output() testComplete = new EventEmitter<TestResult>();
  @ViewChild('nameEl') nameRef?: ElementRef<HTMLInputElement>;

  phase: Phase = 'free';

  freeName = '';
  freeObject = '';
  freeColor = '';
  freePlace = '';

  cuedQuestions: CuedQuestion[] = [];
  cuedAnswers: Partial<Record<FieldKey, string>> = {};
  // Handoff 2026-08-17: the cue is one screen per field, so only the current
  // question is shown; picking an option locks it and advances.
  cuedIndex = 0;
  private advancing = false;

  freeScore = 0;
  cuedScore = 0;

  /**
   * What she is about to hand in, held while the summary is on screen
   * (correction brief 21.5). The answers used to go straight through on the last
   * tap, so the final one was never seen twice and a mis-tap on a three-option
   * cue was unrecoverable and invisible.
   */
  private pendingFree: RecallResponses | null = null;
  private pendingCued: Partial<RecallResponses> = {};

  colorEntries: { name: string; hex: string }[] = [];
  placeOptions: string[] = [];

  private startTime = 0;

  constructor(
    private state: SharpStateService,
    private scoring: SharpScoringService,
    private audio: SharpAudioService,
    private translation: TranslationService
  ) {}

  ngOnInit(): void {
    this.startTime = Date.now();
    // Swatches = the recall colour pool (the six AA colours), in pool order, so
    // the picker never offers a colour that can't be the answer.
    this.colorEntries = RECALL_POOLS.color.map((name) => ({
      name,
      hex: COLORS_MAP[name],
    }));
    this.placeOptions = [...RECALL_POOLS.place];
    setTimeout(() => this.nameRef?.nativeElement?.focus(), 200);
  }

  ngOnDestroy(): void {}

  get recallItems(): RecallItems {
    return this.state.recallItems;
  }

  pickColor(name: string): void {
    this.freeColor = name;
  }

  pickPlace(p: string): void {
    this.freePlace = p;
  }

  translatePool(field: FieldKey, value: string): string {
    if (!value) return '';
    const lc = value.toLowerCase();
    const key = `cognitive.sharpV2.recall.${this.poolKey(field)}.${lc}`;
    return this.translation.translate(key, { defaultValue: value });
  }

  private poolKey(field: FieldKey): string {
    return this.poolKeyFor(field);
  }

  poolKeyFor(field: FieldKey): string {
    return field === 'name'
      ? 'names'
      : field === 'color'
      ? 'colors'
      : field === 'place'
      ? 'places'
      : 'objects';
  }

  getColorHex(name: string): string {
    return COLORS_MAP[name] || '#888';
  }

  allCuedAnswered(): boolean {
    return this.cuedQuestions.every((q) => !!this.cuedAnswers[q.key]);
  }

  private matchText(field: FieldKey, input: string): boolean {
    if (!input) return false;
    const target = this.recallItems[field];
    const a = input.trim().toLowerCase();
    if (!a) return false;
    if (this.heMatch(a, target.toLowerCase())) return true;
    const translated = this.translatePool(field, target).toLowerCase();
    if (translated && this.heMatch(a, translated)) return true;
    return false;
  }

  // This is a MEMORY test, not a spelling test. Hebrew ktiv male/chaser must not
  // fail recall: "מטריה" for "מטרייה", "ספריה" for "הספרייה", "בית מרקחת" for
  // "בית המרקחת" all count as remembered. Normalise both sides (exact after
  // normalisation, else the existing fuzzy tolerance). Non-Hebrew input is left
  // untouched so English matching is unchanged, and it is not fuzzy matching:
  // "כובע" vs "מטרייה" stays wrong.
  private heMatch(a: string, b: string): boolean {
    const na = this.normHe(a);
    const nb = this.normHe(b);
    if (na === nb) return true;
    return this.scoring.fuzzyMatch(na, nb);
  }

  private normHe(s: string): string {
    return (s || '')
      .replace(/[֑-ׇ]/g, '') // niqqud + te'amim
      .replace(/[׳״'"]/g, '') // geresh, gershayim, quotes
      .trim()
      .split(/\s+/)
      .map((w) => w.replace(/^ה/, '')) // definite article ה on each word
      .join(' ')
      .replace(/יי/g, 'י') // doubled yod: מטרייה -> מטריה
      .replace(/וו/g, 'ו'); // doubled vav
  }

  submitFree(skipped = false): void {
    const resp: RecallResponses = {
      name: skipped ? '' : this.freeName.trim().toLowerCase(),
      color: skipped ? '' : this.freeColor,
      object: skipped ? '' : this.freeObject.trim().toLowerCase(),
      place: skipped ? '' : this.freePlace,
    };

    let score = 0;
    if (this.matchText('name', resp.name)) score++;
    if (resp.color && resp.color === this.recallItems.color) score++;
    if (this.matchText('object', resp.object)) score++;
    if (resp.place && resp.place === this.recallItems.place) score++;

    this.freeScore = score;

    // Correction brief 21.2: the keyboard stays up through the phase change
    // otherwise, covering the cue options she is meant to choose between.
    (document.activeElement as HTMLElement | null)?.blur?.();

    if (score === 4) {
      this.cuedScore = 4;
      this.openReview(resp, {});
      return;
    }

    this.buildCuedQuestions(resp);
    this.cuedIndex = 0;
    this.phase = 'cued';
  }

  get currentCued(): CuedQuestion | null {
    return this.cuedQuestions[this.cuedIndex] || null;
  }

  private buildCuedQuestions(fr: RecallResponses): void {
    const missed: FieldKey[] = [];
    if (!this.matchText('name', fr.name)) missed.push('name');
    if (!fr.color || fr.color !== this.recallItems.color) missed.push('color');
    if (!this.matchText('object', fr.object)) missed.push('object');
    if (!fr.place || fr.place !== this.recallItems.place) missed.push('place');

    this.cuedQuestions = missed.map((key) => {
      const correct = this.recallItems[key];
      // Handoff 2026-08-17: the cue is 3-option (was 2) - the target plus two
      // distinct foils, shuffled so the target is not always first.
      const bag = RECALL_POOLS[key].filter((x) => x !== correct);
      const foils: string[] = [];
      while (foils.length < 2 && bag.length) {
        foils.push(bag.splice(this.scoring.randInt(0, bag.length - 1), 1)[0]);
      }
      const options = [correct, ...foils];
      for (let i = options.length - 1; i > 0; i--) {
        const j = this.scoring.randInt(0, i);
        [options[i], options[j]] = [options[j], options[i]];
      }
      return { key, options };
    });
  }

  pickCued(key: FieldKey, val: string): void {
    if (this.advancing) return;
    this.cuedAnswers[key] = val;
    // Lock the answer and move to the next missed field (or finish).
    this.advancing = true;
    setTimeout(() => {
      this.advancing = false;
      if (this.cuedIndex < this.cuedQuestions.length - 1) {
        this.cuedIndex++;
      } else {
        this.submitCued();
      }
    }, 260);
  }

  submitCued(): void {
    let score = this.freeScore;
    for (const [key, val] of Object.entries(this.cuedAnswers)) {
      if (val === this.recallItems[key as FieldKey]) score++;
    }
    this.cuedScore = score;
    const fr: RecallResponses = {
      name: this.freeName.trim().toLowerCase(),
      color: this.freeColor,
      object: this.freeObject.trim().toLowerCase(),
      place: this.freePlace,
    };
    this.openReview(fr, { ...this.cuedAnswers });
  }

  // ---- summary before continuing (correction brief 21.5) ----
  private openReview(
    freeResp: RecallResponses,
    cued: Partial<RecallResponses>
  ): void {
    this.pendingFree = freeResp;
    this.pendingCued = cued;
    this.phase = 'review';
  }

  /**
   * Her final answer per field, for the summary. The cued pick wins where there
   * is one, because that is the answer she actually handed in for that field.
   * Deliberately shows no correct/incorrect marking: this is a scored recall, and
   * a tick here would read as feedback in the middle of the battery.
   */
  get summaryRows(): { key: FieldKey; value: string; color: string | null }[] {
    const keys: FieldKey[] = ['name', 'color', 'object', 'place'];
    const free = this.pendingFree;
    return keys.map((key) => {
      const raw = (this.pendingCued[key] as string) || (free?.[key] as string) || '';
      if (!raw) return { key, value: '', color: null };
      if (key === 'color') return { key, value: this.translatePool(key, raw), color: this.getColorHex(raw) };
      // a free-text answer is hers verbatim; a pool value gets its label
      const label = this.translatePool(key, raw);
      return { key, value: label || raw, color: null };
    });
  }

  confirmReview(): void {
    if (!this.pendingFree) return;
    this.finish(this.pendingFree, this.pendingCued);
  }

  private finish(
    freeResp: RecallResponses,
    cued: Partial<RecallResponses>
  ): void {
    this.phase = 'done';
    this.audio.success();
    // Step2 change5: record which fields were recalled freely vs only via cue,
    // using the same matching used for scoring (fuzzy for name/object, exact for
    // colour/place). The results screen names these exact items.
    const keys: FieldKey[] = ['name', 'color', 'object', 'place'];
    const isFree = (k: FieldKey): boolean =>
      k === 'name' || k === 'object'
        ? this.matchText(k, (freeResp[k] as string) || '')
        : !!freeResp[k] && freeResp[k] === this.recallItems[k];
    const freeItems = keys.filter(isFree);
    const cuedItems = keys.filter(
      (k) => !isFree(k) && cued[k] != null && cued[k] === this.recallItems[k]
    );
    const response: DelayedRecallResult = {
      recallItems: { ...this.recallItems },
      freeResponses: freeResp,
      cuedResponses: cued,
      freeScore: this.freeScore,
      cuedScore: this.cuedScore,
      cueBenefit: this.cuedScore - this.freeScore,
      freeItems,
      cuedItems,
    };
    const result: TestResult = {
      testId: this.testId,
      response,
      correct: this.cuedScore >= 3,
      responseTime: Date.now() - this.startTime,
      timestamp: Date.now(),
    };
    this.testComplete.emit(result);
  }

  colorLabelKey(name: string): string {
    return `cognitive.sharpV2.recall.colors.${name}`;
  }

  placeLabelKey(name: string): string {
    return `cognitive.sharpV2.recall.places.${name.toLowerCase()}`;
  }
}
